import { Button, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Box, Stack } from "@mui/system";
import SiteWideText from "./SettingWebsiteElements/SiteWideText";
import DefaultAppearance from "./SettingWebsiteElements/DefaultAppearance";
import Features from "./SettingWebsiteElements/Features";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { websiteActionEndPointLogic } from "../../../../../logic/AssetManagement/Websites/websiteActionEndPointLogic";
import { pageBuildingEndPointLogic } from "../../../../../logic/AssetManagement/PageManagement/PageBuilding/pageBuildingEndPointLogic";
import {
  selectUserDetails,
  selectUserToken,
} from "../../../../../features/userSlice";
import { useSelector } from "react-redux";
import { DesignThemeEndPointLogic } from "../../../../../logic/AssetManagement/Design/Theme/DesignThemeEndPointLogic";
import {
  isEmpty,
  validateAlphaNumeric,
  validateColor,
  validateLatency,
  validateMax,
  validateMin,
  validatePercentages,
  validateText,
  validateWebsiteName,
  validateUrl,
} from "../../../../../utils/websitesValidation";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";

export interface FormData {
  [key: string]: string | number | any; // Key-value pairs for form data
}

const startingTier = [
  { shared_uid: null, label: "No Special Offer" },
  { shared_uid: 1, label: "Tier 1" },
  { shared_uid: 2, label: "Tier 2" },
  { shared_uid: 3, label: "Tier 3" },
  { shared_uid: 4, label: "Tier 4" },
  { shared_uid: 5, label: "Tier 5" },
  { shared_uid: 6, label: "Tier 6" },
];

//-- required fields for validation
const requiredFields = [
  "site_title",
  "default_theme_shared_uid",
  "default_popup_shared_uid",
  "cta_button_color",
  "cta_button_text",
  "banner_desktop",
  "banner_tablet",
  "banner_mobile",
  "rush_pick_effect_percentage",
  "rush_pick_latency",
  "rush_pick_min_position",
  "rush_pick_max_position",
];

function WebsiteSetting() {
  const navigate = useNavigate();
  const [, setWebsiteList] = useState([]);
  const isSuperUser = useSelector(selectUserDetails) as any;
  const [loading, setLoading] = useState(false);
  const [selectedWebsiteShareuid, setSelectedWebsiteShareuid] = useState(
    null
  ) as any;
  const [settingsAttributes, setSettingsAttributes] = useState([]) as any;
  const [siteAllAvailableWidgets, setSiteAllAvailableWidgets] = useState([]);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [searchParams] = useSearchParams() as any;

  const [tierList, setTierList] = useState([]) as any;
  const [designThemeData, setDesignThemeData] = useState([]) as any;

  //-- initialize error messages
  const [formError, setFormError] = useState({
    site_title: "",
    default_theme_shared_uid: "",
    default_popup_shared_uid: "",
    cta_button_color: "",
    cta_button_text: "",
    banner_desktop: "",
    banner_tablet: "",
    banner_mobile: "",
    rush_pick_effect_percentage: "",
    rush_pick_latency: "",
    rush_pick_min_position: "",
    rush_pick_max_position: "",
  });

  // -- Set TierList
  useEffect(() => {
    setTierList(startingTier);
  }, [siteAllAvailableWidgets]);

  useEffect(() => {
    setSelectedWebsiteShareuid(searchParams.get("websiteSharedUid"));
    // eslint-disable-next-line
  }, []);

  //-- Initialize Website Setting Data
  const [webSiteValues, setWebSiteValues] = useState<FormData>({
    site_title: "",
    advertiser_disclosure: "",
    responsible_gambling: "",
    copyright: "",
    headline_mobile: "",
    headline_tablet: "",
    headline_desktop: "",
    default_theme_shared_uid: "",
    default_popup_shared_uid: "",
    cta_button_color: "",
    cta_button_text: "",
    banner_desktop: "",
    banner_tablet: "",
    banner_mobile: "",
    rush_pick_effect_percentage: "",
    rush_pick_latency: "",
    rush_pick_min_position: "",
    rush_pick_max_position: "",
    special_offer_shared_uid: "",
    special_offer_tier_id: "",
    osano_cm_link: "",
    tracking_domain: "",
    gtm_id: "",
  }) as any;

  //-- Onchange Set Data in WebSiteValues State
  const handleChange = (name: string, value: any) => {
    setWebSiteValues((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    let errorMsg = "";
    if (name === "site_title") {
      errorMsg = validateWebsiteName(value);
    } else if (name === "default_theme_shared_uid") {
      errorMsg = validateAlphaNumeric(value);
    } else if (name === "default_popup_shared_uid") {
      errorMsg = validateAlphaNumeric(value);
    } else if (name === "cta_button_color") {
      errorMsg = validateColor(value);
    } else if (name === "cta_button_text") {
      errorMsg = validateText(value);
    } else if (name === "banner_desktop") {
      errorMsg = isEmpty(value);
    } else if (name === "banner_tablet") {
      errorMsg = isEmpty(value);
    } else if (name === "banner_mobile") {
      errorMsg = isEmpty(value);
    } else if (name === "rush_pick_effect_percentage") {
      errorMsg = validatePercentages(value);
    } else if (name === "rush_pick_latency") {
      errorMsg = validateLatency(value);
    } else if (name === "rush_pick_min_position") {
      errorMsg = validateMin(value, webSiteValues?.rush_pick_max_position);
    } else if (name === "rush_pick_max_position") {
      errorMsg = validateMax(value, webSiteValues?.rush_pick_min_position);
    } else if (name === "osano_cm_link") {
      errorMsg = validateUrl(value);
    } else if (name === "tracking_domain") {
      errorMsg = validateUrl(value);
    }

    setFormError((prevErrors: any) => ({
      ...prevErrors,
      [name]: errorMsg,
    }));
  };

  //-- Get Website Setting Data From API
  useEffect(() => {
    const getWebsiteSettingData = async (
      oauthTokenHeader: any,
      selectedWebsiteShareuid: any
    ) => {
      try {
        setLoading(true);
        await websiteActionEndPointLogic.getWebsitesSettingData(
          oauthTokenHeader,
          setSettingsAttributes,
          selectedWebsiteShareuid,
          navigate,
          searchParams
        );
      } catch (error: any) {
        console.error("Error fetching Websites Setting data", error);
      } finally {
        setLoading(false);
      }
    };

    selectedWebsiteShareuid &&
      getWebsiteSettingData(oauthTokenHeader, selectedWebsiteShareuid);
    // eslint-disable-next-line
  }, [selectedWebsiteShareuid, oauthTokenHeader]);

  //-- Get All Widgets From API
  useEffect(() => {
    const getWidgets = async () => {
      try {
        setLoading(true);
        await pageBuildingEndPointLogic.getWidgets(
          selectedWebsiteShareuid,
          oauthTokenHeader,
          setSiteAllAvailableWidgets
        );
      } catch (error: any) {
        console.error("Error fetching Widgets data", error);
      } finally {
        setLoading(false);
      }
    };
    selectedWebsiteShareuid && getWidgets();
  }, [selectedWebsiteShareuid, oauthTokenHeader]);

  useEffect(() => {
    const getThemeData = async () => {
      try {
        setLoading(true);
        await DesignThemeEndPointLogic.getDesignTheme(
          oauthTokenHeader,
          selectedWebsiteShareuid,
          setDesignThemeData
        );
      } catch (error: any) {
        console.error("Error fetching design data", error);
      } finally {
        setLoading(false);
      }
    };
    selectedWebsiteShareuid && getThemeData();
  }, [selectedWebsiteShareuid, oauthTokenHeader]);

  //-- Set SettingsAttributes API Data in WebSiteValues State
  useEffect(() => {
    if (settingsAttributes.length > 0) {
      const mergedValues = {
        ...webSiteValues, // Retain existing values
        site_title: settingsAttributes[0]?.site_title || "",
        advertiser_disclosure:
          settingsAttributes[0]?.advertiser_disclosure || "",
        responsible_gambling: settingsAttributes[0]?.responsible_gambling || "",
        copyright: settingsAttributes[0]?.copyright || "",
        headline_mobile: settingsAttributes[0]?.headline_mobile || "",
        headline_tablet: settingsAttributes[0]?.headline_tablet || "",
        headline_desktop: settingsAttributes[0]?.headline_desktop || "",
        cta_button_color: settingsAttributes[0]?.cta_button_color || "",
        cta_button_text: settingsAttributes[0]?.cta_button_text || "",
        banner_desktop: settingsAttributes[0]?.banner_desktop || "",
        banner_tablet: settingsAttributes[0]?.banner_tablet || "",
        banner_mobile: settingsAttributes[0]?.banner_mobile || "",
        rush_pick_effect_percentage:
          settingsAttributes[0]?.rush_pick_effect_percentage || 0,
        rush_pick_latency: settingsAttributes[0]?.rush_pick_latency || 0,
        rush_pick_min_position:
          settingsAttributes[0]?.rush_pick_min_position || "",
        rush_pick_max_position:
          settingsAttributes[0]?.rush_pick_max_position || "",
        osano_cm_link: settingsAttributes[0]?.osano_cm_link || null,
        tracking_domain: settingsAttributes[0]?.tracking_domain || null,
        gtm_id: settingsAttributes[0]?.gtm_id || null,
      };

      setWebSiteValues(mergedValues); // Update the state
      //-- Dynamically set theme_debug property For Super User
      if (isSuperUser?.superuser) {
        setWebSiteValues((prev: any) => ({
          ...prev,
          theme_debug: settingsAttributes[0]?.theme_debug ?? 0,
        }));
      }
    }
    // eslint-disable-next-line
  }, [settingsAttributes]);

  //-- Get Default theme and Popup Id
  useEffect(() => {
    if (designThemeData.length > 0) {
      const theme = designThemeData.find(
        (item: any) =>
          item.name === settingsAttributes[0]?.default_theme__theme_meta_name
      );
      setWebSiteValues((prev: any) => ({
        ...prev,
        default_theme_shared_uid: theme?.shared_uid || "",
      }));
    }

    if (siteAllAvailableWidgets.length > 0) {
      const filteredWidgets = siteAllAvailableWidgets?.filter(
        (widget: any) => widget?.type === "Popup Offer"
      );
      const defaultPopup = filteredWidgets.find(
        (item: any) =>
          item.widget_name ===
          settingsAttributes[0]?.default_popup__popup_meta_name
      ) as any;
      setWebSiteValues((prev: any) => ({
        ...prev,
        default_popup_shared_uid: defaultPopup?.shared_uid || "",
      }));
    }
    // eslint-disable-next-line
  }, [designThemeData, siteAllAvailableWidgets]);

  //-- Get Special Offer Popup ID From Popup Name and Set Special Offer Popup ID into WebSiteValues
  useEffect(() => {
    if (siteAllAvailableWidgets.length > 0) {
      const filteredWidgets = siteAllAvailableWidgets?.filter(
        (widget: any) => widget?.type === "Popup Offer"
      );
      const specialOfferPopup = filteredWidgets?.find(
        (item: any) =>
          item.widget_name ===
          settingsAttributes[0]?.special_offer_popup_id__popup_meta_name
      ) as any;
      setWebSiteValues((prev: any) => ({
        ...prev,
        special_offer_shared_uid: specialOfferPopup?.shared_uid || null,
      }));
    }
    // eslint-disable-next-line
  }, [
    siteAllAvailableWidgets,
    // eslint-disable-next-line
    settingsAttributes[0]?.special_offer_popup_id__popup_meta_name,
  ]);

  //-- Get Special Offer Tier ID From Tier Name and set tier Id into WebSiteValues
  useEffect(() => {
    if (tierList.length > 0) {
      const tier = tierList.find(
        (item: any) =>
          item.label === settingsAttributes[0]?.special_offer_tier_id__name
      );
      setWebSiteValues((prev: any) => ({
        ...prev,
        special_offer_tier_id: tier?.shared_uid || null,
      }));
    }
    // eslint-disable-next-line
  }, [tierList, settingsAttributes[0]?.special_offer_tier_id__name]);

  //-- Update Setting Data
  const updateSettingsData = async () => {
    try {
      setLoading(true);
      const res = await websiteActionEndPointLogic.updateWebsitesSettingData(
        webSiteValues,
        selectedWebsiteShareuid,
        oauthTokenHeader
      );

      await websiteActionEndPointLogic.getAllWebsites(
        oauthTokenHeader,
        setWebsiteList
      );
      if (res === "success") {
        navigate("/assets/websites/website_actions"); // Redirect to the Website List
      } else {
        return;
      }
    } catch (error: any) {
      console.error("Error fetching Websites Setting data", error);
    } finally {
      setLoading(false);
    }
  };

  //-- Check if form is valid (all fields must pass validation)
  const isFormValid =
    Object.values(formError).every((err: any) => err === "") &&
    Object.values(requiredFields).every(
      (key: any) => webSiteValues[key]?.toString().trim() !== ""
    ) &&
    webSiteValues.rush_pick_latency !== 0;

  return (
    <>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Paper sx={{ padding: "16px 16px" }}>
          <Typography fontSize={24}>Website Settings</Typography>
          <Box>
            <Grid container mt={2}>
              <Grid xs={4} sx={{ pr: 2 }}>
                <SiteWideText
                  webSiteValues={webSiteValues}
                  handleChange={handleChange}
                  formError={formError}
                />
              </Grid>
              <Grid xs={4} sx={{ pl: 2, borderLeft: "1px solid #E0E0E0" }}>
                <DefaultAppearance
                  siteAllAvailableWidgets={siteAllAvailableWidgets}
                  designThemeData={designThemeData}
                  webSiteValues={webSiteValues}
                  handleChange={handleChange}
                  oauthTokenHeader={oauthTokenHeader}
                  websiteShareuid={selectedWebsiteShareuid}
                  formError={formError}
                />
                <Features
                  siteAllAvailableWidgets={siteAllAvailableWidgets}
                  tierList={tierList}
                  superUser={isSuperUser?.superuser}
                  webSiteValues={webSiteValues}
                  handleChange={handleChange}
                  formError={formError}
                  setFormError={setFormError}
                />
              </Grid>
              <Grid
                xs={4}
                sx={{
                  pl: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                }}
              >
                <Button
                  data-tracker="44"
                  variant="contained"
                  onClick={updateSettingsData}
                  disabled={!isFormValid}
                  color="secondary"
                  sx={{ display: "flex" }}
                >
                  <Stack alignItems={"center"} flexDirection={"row"} gap={1}>
                    Save Changes
                    <SaveOutlinedIcon />
                  </Stack>
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}
    </>
  );
}

export default WebsiteSetting;
